import React, { useState } from 'react';
import { Copy, Plus, Trash2, MailCheck, RefreshCw, Globe, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
interface InboxHeroProps {
  address: string;
  onRefresh: () => void;
  onNew: () => void;
  onDelete: () => void;
  onOpenSetup: () => void;
  isLoading?: boolean;
}
export function InboxHero({ address, onRefresh, onNew, onDelete, onOpenSetup, isLoading }: InboxHeroProps) {
  const [copied, setCopied] = useState(false);
  const copyToClipboard = async () => {
    if (!address) return;
    const sanitizedAddress = address.trim().toLowerCase();
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(sanitizedAddress);
      } else {
        // Fallback for non-secure contexts or restricted environments
        const textArea = document.createElement("textarea");
        textArea.value = sanitizedAddress;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        textArea.style.top = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }
      setCopied(true);
      toast.success('Đã sao chép địa chỉ!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Clipboard copy failed:', err);
      toast.error('Không thể sao chép. Vui lòng tự bôi đen và copy.');
    }
  };
  return (
    <div className="relative py-12 md:py-16 overflow-hidden">
      <div className="hero-glow" />
      <div className="text-center space-y-6 max-w-3xl mx-auto px-4">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-balance leading-tight">
          Sử dụng <span className="text-gradient-purple">email ảo</span> cho thế giới thật
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground">
          Bảo vệ quyền riêng tư với Cloudflare. Kết nối domain riêng hoặc sử dụng địa chỉ tạm thời ngay lập tức.
        </p>
        <div className="mt-10 group relative max-w-2xl mx-auto">
          <div className="absolute -inset-1 bg-gradient-to-r from-primary to-purple-600 rounded-xl blur opacity-25 group-hover:opacity-50 transition duration-1000" />
          <div
            onClick={copyToClipboard}
            className="relative flex items-center justify-between px-6 py-5 bg-card border border-white/10 rounded-xl cursor-pointer hover:border-primary/50 transition-all active:scale-[0.99] group/addr"
          >
            <div className="flex items-center gap-3 overflow-hidden pr-4">
              <MailCheck className={`h-6 w-6 shrink-0 transition-colors ${copied ? 'text-green-500' : 'text-primary'}`} />
              <span className="text-lg md:text-2xl font-mono font-medium truncate break-all text-foreground group-hover/addr:text-primary transition-colors">
                {address || "Đang khởi tạo..."}
              </span>
            </div>
            {copied ? (
              <Check className="h-5 w-5 text-green-500 shrink-0" />
            ) : (
              <Copy className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
            )}
          </div>
          {address && (
            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 text-xs text-muted-foreground font-medium animate-pulse opacity-75">
              <Loader2 className="h-3 w-3 animate-spin" />
              Đang lắng nghe email đến...
            </div>
          )}
        </div>
        <div className="flex flex-wrap justify-center gap-4 pt-8">
          <Button onClick={onNew} className="btn-purple gap-2 py-6 px-8 text-md font-semibold" disabled={isLoading}>
            <Plus className="h-5 w-5" />
            Tạo Inbox mới
          </Button>
          <Button onClick={onOpenSetup} variant="outline" className="glass-card gap-2 py-6 px-8 text-md font-semibold border-primary/20 hover:bg-primary/5">
            <Globe className="h-5 w-5 text-primary" />
            Domain riêng
          </Button>
          <div className="w-full sm:w-auto flex gap-4">
            <Button onClick={onRefresh} variant="ghost" className="flex-1 sm:flex-none gap-2 py-6 px-6" disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              Làm mới
            </Button>
            <Button onClick={onDelete} variant="ghost" className="flex-1 sm:flex-none gap-2 py-6 px-6 text-destructive hover:text-destructive hover:bg-destructive/10" disabled={!address || isLoading}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}